import React, { useMemo } from "react";
import _ from "lodash";
import moment from "moment";

import { useRouteMatch } from "react-router";
import { useMutation, useQuery } from "@apollo/client";

import { 
  CreateMarathonEventInput,
CreateMarathonEventMutation,
CreateMarathonEventMutationVariables,
GetAllCategoriesForMarathonQuery,
GetAllMarathonEventsQuery,
} from "../../../../../__gqltypes__";
import { MarathonEventData } from "./components/MarathonEventData";
import { EventTabContainer } from "../../general/EventTabContainer";
import { CREATE_MARATHON_EVENT, GET_ALL_MARATHON_CATEGORIES, GET_ALL_MARATHON_EVENTS } from "./graphql";

const baseURL = '/sp3/liveops/marathon';
type MarathonEventConfig = GetAllMarathonEventsQuery['liveOps']['getAllMarathonEvents'][0];
type MarathonCategoryData = GetAllCategoriesForMarathonQuery['liveOps']['getAllCategoriesForMarathon'];

export default function Marathon(): JSX.Element {
  const [marathonEvents, setMarathonEvents] = React.useState<Array<MarathonEventConfig>>([]);
  const match = useRouteMatch();
    const skip = React.useMemo(() => {
      return !match.path.includes(baseURL);
    }, [match.path]);
    const { data: marathonData, loading: eventsLoading } = useQuery<GetAllMarathonEventsQuery>(
      GET_ALL_MARATHON_EVENTS,
      {
        skip,
        fetchPolicy: 'no-cache',
      }
    );

    const [createMarathon, { loading: createMLoading}] = useMutation<
      CreateMarathonEventMutation, 
      CreateMarathonEventMutationVariables
      >(CREATE_MARATHON_EVENT);

    const {data: marathonCategories } = useQuery<GetAllCategoriesForMarathonQuery>(GET_ALL_MARATHON_CATEGORIES, {
      skip,
    });

    const categoryData: MarathonCategoryData = useMemo(() => {
      if (!marathonCategories || !marathonCategories.liveOps.getAllCategoriesForMarathon) {
        return [];
      }
      return marathonCategories.liveOps.getAllCategoriesForMarathon;
    }, [marathonCategories]);
    
    const handleUpsert = React.useCallback((event: MarathonEventConfig) => {
      const other = marathonEvents.filter(({id}) => id !== event.id);
      const all = [...other, event];
      setMarathonEvents(_.orderBy(all, 'startDate', 'desc'));
    }, [marathonEvents, setMarathonEvents]);

    const handleAddMarathon = React.useCallback((input: CreateMarathonEventInput) => {
      return new Promise<string | null>((resolve, reject) => {
          createMarathon({
          variables: {
            input
          }
        }).then(({data}) => {
          const newEvent = data?.createMarathonEvent?.event;
          if (newEvent) {
            handleUpsert(newEvent);
            resolve(newEvent.id);
          } else {
            resolve(null);
          }
        }).catch((error) => {
          reject(error);
          console.error(error);
        });
      });
    }, [createMarathon, handleUpsert]);

    const handleRemoveMarathon = React.useCallback((eventId: string) => {
      const leftEvents = marathonEvents.filter(({id}) => id !== eventId);
      setMarathonEvents(leftEvents);
    }, [marathonEvents, setMarathonEvents]);

    const eventComponent = React.useCallback((eventId: string) => {
      return <MarathonEventData
        eventId={eventId}
        onUpserEvent={handleUpsert}
        onRemoveEvent={handleRemoveMarathon}
        categoryData={categoryData}
        />;
    }, [handleRemoveMarathon, handleUpsert, categoryData]);

    React.useEffect(() => {
      if (marathonData && marathonData.liveOps) {
        const _mData = _.orderBy(marathonData.liveOps.getAllMarathonEvents, 'startDate', 'desc');
        setMarathonEvents(_.compact(_mData));
      }
    }, [marathonData]);

    const loading = eventsLoading || createMLoading;
    return <EventTabContainer 
      title="Marathon"
      baseURL={baseURL}
      events={marathonEvents}
      eventsLoading={eventsLoading}
      loading={loading}
      onAddEvent={handleAddMarathon}
      eventComponent={eventComponent}
      />;
}